import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, AlertTriangle, BarChart3, Send, Eye } from 'lucide-react';

export default function DigestPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get('/digest/preview');
      setPreview(res.data.data);
    } catch { toast.error('Failed to load digest preview'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); }, []);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await api.post('/digest/send');
      setSendResult(res.data.data);
      toast.success(`Digest sent to ${res.data.data.sent_count} recipient(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to send digest');
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="digest-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Mail className="h-6 w-6" /> Daily Digest</h1>
          <p className="text-muted-foreground mt-1">Preview and send the daily summary email to admin users</p>
        </div>
        <Button onClick={handleSend} disabled={sending} data-testid="send-digest-btn">
          <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send Digest Now'}
        </Button>
      </div>

      {sendResult && (
        <Card className={sendResult.sent_count > 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}>
          <CardContent className="py-3 px-4">
            <p className="text-sm font-medium">{sendResult.sent_count > 0 ? `Digest sent to ${sendResult.sent_count} recipient(s)` : 'No recipients received the digest'}</p>
            {sendResult.failed_count > 0 && <p className="text-sm text-red-600">{sendResult.failed_count} failed</p>}
          </CardContent>
        </Card>
      )}

      {preview && (
        <>
          {/* Action Required */}
          <Card className={preview.action_items.length > 0 ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-emerald-500'}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${preview.action_items.length > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                Action Required
              </CardTitle>
              <CardDescription>{preview.action_items.length > 0 ? `${preview.action_items.length} item(s) need attention` : 'No immediate actions required'}</CardDescription>
            </CardHeader>
            <CardContent>
              {preview.action_items.length === 0 ? (
                <p className="text-sm text-emerald-600 py-2">All clear — no action items today.</p>
              ) : (
                <div className="space-y-2">
                  {preview.action_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`action-item-${item.type}`}>
                      <div className="flex items-center gap-3">
                        <Badge variant={item.priority === 'high' ? 'destructive' : 'secondary'} className="text-[10px]">{item.priority}</Badge>
                        <span className="text-sm">{item.message}</span>
                      </div>
                      <span className="font-mono text-sm font-semibold">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Platform Summary */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" /> Platform Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {preview.summary_items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`summary-item-${item.type}`}>
                    <span className="text-sm">{item.message}</span>
                    <span className="font-mono text-sm font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" /> Digest Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Recipient:</span> <span className="font-medium">{preview.recipient_name}</span></div>
                <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{preview.recipient}</span></div>
                <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{preview.date}</span></div>
                <div><span className="text-muted-foreground">Schedule:</span> <span className="font-medium">Manual trigger (configurable)</span></div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
